import type { FC, PropsWithChildren } from 'react'
import styled from '@emotion/styled'

import ColumnTrack from '@segment/matcha/components/ColumnTrack'
import IconWrapper from '@segment/matcha/components/IconWrapper'
import { CompliantIcon, NamingConventionsIcon } from '@segment/matcha/components/Icons/Brandbook'
import { ChevronRightIcon } from '@segment/matcha/components/Icons/Common'

import CopyTextBase, * as CopyText from '@segment/matcha/recipes/CopyText'
import SectionBase, * as Section from '@segment/matcha/recipes/Section'
import CardBase, * as Card from '@segment/matcha/recipes/Card'

import getAbsoluteUrl from '@segment/utils/getAbsoluteUrl'

import { usePageConfig } from '@segment/contexts/page'

const copy = {
  heading: `Additional resources`,
}

const cards = [
  {
    key: `Privacy-Compliance`,
    icon: <CompliantIcon />,
    title: `Privacy & Compliance`,
    description: `Automatically detect and classify PII in real-time. By setting rules to proactively block, hash, and routine sensitive data, you can accelerate compliance with GDPR, CCPA, and more.`,
    url: `/product/privacy-portal/`,
  },
  {
    key: `Standardize-your-data`,
    icon: <NamingConventionsIcon />,
    title: `Standardize your data`,
    description: `Standardize data collection by creating a single tracking plan across all your web and mobile apps.`,
    url: `/product/protocols/`,
  },
]

const AdditionalResources: FC<PropsWithChildren<unknown>> = () => {
  const pageConfig = usePageConfig()

  return (
    <StyledSection variant="centered">
      <Section.Copy>
        <CopyTextBase centered={true}>
          <CopyText.Heading variant="h2" tag="h2">
            {copy.heading}
          </CopyText.Heading>
        </CopyTextBase>
      </Section.Copy>
      <Section.Contents>
        <Body>
          <ColumnTrack columns={1} overrides={{ md: 2 }}>
            {cards.map((card) => (
              <CardBase key={card.key} href={getAbsoluteUrl(card.url, pageConfig)}>
                <Card.Body>
                  <Card.Accessories>
                    <Card.AccessoriesRow>
                      <IconWrapper variant="transparent">{card.icon}</IconWrapper>
                    </Card.AccessoriesRow>
                  </Card.Accessories>
                  <Card.Copy>
                    <Card.Headline tag="h3">{card.title}</Card.Headline>
                    <Card.Description variant="pLarge">{card.description}</Card.Description>
                  </Card.Copy>
                  <Card.Action alignment="right">
                    <ChevronRightIcon />
                  </Card.Action>
                </Card.Body>
              </CardBase>
            ))}
          </ColumnTrack>
        </Body>
      </Section.Contents>
    </StyledSection>
  )
}

const StyledSection = styled(SectionBase)`
  ${Section.Wrapper} {
    padding-bottom: 120px;

    row-gap: 40px;
    ${({ theme }) => theme.breakpoints.up(`md`)} {
      row-gap: 64px;
    }
  }
`

const Body = styled.div`
  max-width: 960px;
  margin: 0 auto;
`

export default AdditionalResources
